/**
 * Einstellungen der Lohnverrechnung (AP-22-B) — aufgebaut wie im Prototyp:
 * Reiter je Gruppe, jedes Feld mit Beschriftung, Einheit und Vorgabewert, die
 * Erklärung hinter dem „?".
 *
 * **Versioniert über ein gemeinsames „Gültig ab".** Die Maske zeigt, was an
 * diesem Tag gilt (eigener Satz, sonst der Standard der KV-Vorlage — AP-22 Teil 6). Eine Änderung
 * schreibt einen Satz genau zu diesem Datum: Gibt es ihn schon, wird er
 * korrigiert, sonst neu angelegt. Frühere Sätze bleiben stehen, damit gerechnete
 * Monate nachrechenbar bleiben. Ein geleertes Feld entfernt den Satz dieses
 * Datums; dann gilt wieder der vorige Wert.
 *
 * **Speichern (AP-36, Fall 1):** ohne Knopf. Die Maske ist *eine* Speicherstelle
 * je Stichtag und Kollektivvertrag, ihr Datensatz sind die aufgelösten Werte.
 * `schreibe` zerlegt einen Patch in Sätze (anlegen, korrigieren, entfernen) und
 * antwortet mit der neuen Auflösung. Aufgelöst wird mit `aufloesenEinstellungen`
 * aus `@symphenia/payroll-core` — derselben Regel, mit der der Server rechnet.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { erfuelltRecht } from '@symphenia/shared-types';
import {
  KV_VORLAGEN,
  aufloesenEinstellungen,
  einstellungskatalogFuer,
  kvVorlage,
  parseBetriebszeiten,
  vorlageNaechsterWert,
  vorlageWerte,
  zulageSchluessel,
  type Betriebszeit,
  type KvVorlage,
  type ZulageDef,
  type ZuschlagRegel,
} from '@symphenia/payroll-core';
import { bestaetige } from '@/lib/dialoge';
import { api } from '@/lib/axios';
import { useAuthStore } from '@/lib/auth.store';
import { fieldLabel, inputBase, pageWide, selectBase } from '@/lib/form-styles';
import {
  FeldStatus,
  FeldStatusZeichen,
  mitStand,
  useEntwurf,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Feld,
  type Speicherstelle,
} from '@/lib/speichern';
import {
  createSetting,
  deleteSetting,
  fetchCollectiveAgreements,
  fetchKvTemplate,
  fetchSettings,
  setKvTemplate,
  type KvVorlageStand,
} from '../api/payroll.api';
import { Hilfe } from '../components/Hilfe';
import { AbschlussEinstellung } from '../components/AbschlussEinstellung';
import { ZettelLesenEinstellung } from '../components/ZettelVorschlag';
import { formatIsoDate, todayIso } from '../lib/dates';
import { leseUhrzeit, pruefeUhrzeit } from '../lib/uhrzeit';
import { alsText, alsWert } from '../lib/setting-keys';
import type { CollectiveAgreement, PayrollSetting } from '../types/payroll.types';

type Stelle = Speicherstelle<unknown>;

// Die Grundklassen tragen `w-full` bzw. `block` — hier bestimmt jedes Feld seine Breite selbst.
const eingabe = inputBase.replace('w-full ', '');
const auswahl = selectBase.replace('w-full ', '');
const kopf = fieldLabel.replace('block ', 'inline-flex items-center gap-1 ');

/** Die Schlüssel der Maske: die gemeinsamen und je Zulage der Vorlage Schalter, Satz und Fenster. */
const schluesselFuer = (vorlage: KvVorlage): string[] => einstellungskatalogFuer(vorlage).map((d) => d.key);

const REITER = [
  { id: 'arbeitszeit', label: 'Arbeitszeit' },
  { id: 'ueberstunden', label: 'Überstunden & Zuschläge' },
  { id: 'zulagen', label: 'Zulagen' },
  { id: 'pausen', label: 'Pausen' },
] as const;
type ReiterId = (typeof REITER)[number]['id'];

/** Alle Sätze des Mandanten — der Endpunkt liefert höchstens 200 je Seite. */
async function alleSaetze(): Promise<PayrollSetting[]> {
  const alle: PayrollSetting[] = [];
  for (let page = 1; page <= 50; page++) {
    const res = await fetchSettings({ page, limit: 200 });
    alle.push(...res.data);
    if (res.data.length < 200) break;
  }
  return alle;
}

/** Der Satz, aus dem der Wert am Stichtag stammt — dieselbe Regel wie `aufloesenEinstellungen`. */
function quelle(saetze: PayrollSetting[], key: string, datum: string, kvId: string | null): PayrollSetting | null {
  const gueltig = saetze.filter((s) => s.key === key && s.validFrom <= datum);
  const juengster = (liste: PayrollSetting[]) =>
    liste.reduce<PayrollSetting | null>((a, s) => (!a || s.validFrom > a.validFrom ? s : a), null);
  return (
    (kvId ? juengster(gueltig.filter((s) => s.collectiveAgreementId === kvId)) : null) ??
    juengster(gueltig.filter((s) => s.collectiveAgreementId === null))
  );
}

/** Der nächste Satz nach dem Stichtag — damit niemand überrascht wird, dass sein Wert später wechselt. */
function naechster(saetze: PayrollSetting[], key: string, datum: string, kvId: string | null): PayrollSetting | null {
  return (
    saetze
      .filter(
        (s) =>
          s.key === key &&
          s.validFrom > datum &&
          (s.collectiveAgreementId === null || s.collectiveAgreementId === kvId),
      )
      .sort((a, b) => a.validFrom.localeCompare(b.validFrom))[0] ?? null
  );
}

/** Die Werte zum Stichtag als Datensatz der Speicherstelle — eigene Sätze über dem Standard der Vorlage. */
function aufloesung(saetze: PayrollSetting[], datum: string, kvId: string | null, vorlage: KvVorlage): Datensatz {
  const texte = aufloesenEinstellungen(saetze, datum, kvId, vorlage);
  const daten: Datensatz = {};
  for (const key of schluesselFuer(vorlage)) daten[key] = alsWert(key, texte[key]);
  // Kunst-Stand: Die Stelle übernimmt neue Daten nur, wenn er sich bewegt.
  daten.updatedAt = saetze.reduce((a, s) => (s.updatedAt > a ? s.updatedAt : a), '1970-01-01T00:00:00.000Z');
  return daten;
}

function zahlText(n: number | null | undefined, stellen = 2): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  return n.toLocaleString('de-DE', { maximumFractionDigits: stellen });
}

// ── Seite ───────────────────────────────────────────────────────────────────

export default function PayrollSettingsPage() {
  const authUser = useAuthStore((s) => s.user);
  const gesperrt = !erfuelltRecht('payroll.setting.manage', authUser?.permissions);
  const [params, setParams] = useSearchParams();
  const reiter: ReiterId = REITER.some((r) => r.id === params.get('tab')) ? (params.get('tab') as ReiterId) : 'arbeitszeit';

  const [saetze, setSaetze] = useState<PayrollSetting[] | null>(null);
  const [agreements, setAgreements] = useState<CollectiveAgreement[]>([]);
  const [datum, setDatum] = useState(todayIso());
  const [kvId, setKvId] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Teil 6: die KV-Vorlage des Betriebs — der Standard unter seinen eigenen Werten.
  const [vorlageStand, setVorlageStand] = useState<KvVorlageStand | null>(null);

  const laden = useCallback(async () => {
    try {
      const s = await alleSaetze();
      setSaetze(s);
      return s;
    } catch (err) {
      console.error('Failed to load payroll settings', err);
      setError('Die Einstellungen konnten nicht geladen werden.');
      return null;
    }
  }, []);

  useEffect(() => {
    void laden();
    // Getrennt — Kollektivverträge sind optional (Stufe 1) und dürfen die Seite nicht blockieren.
    fetchCollectiveAgreements()
      .then(setAgreements)
      .catch(() => undefined);
    fetchKvTemplate()
      .then(setVorlageStand)
      .catch(() => undefined);
  }, [laden]);

  // Die Vorlage der gewählten Entlohnungsart, sonst die des Betriebs, sonst die Standardvorlage.
  const artVorlage = agreements.find((kv) => kv.id === kvId)?.templateKey ?? null;
  const vorlage = kvVorlage(artVorlage ?? vorlageStand?.templateKey ?? null);

  const wechsleVorlage = async (schluessel: string) => {
    if (schluessel === vorlage.schluessel) return;
    const neu = kvVorlage(schluessel);
    if (
      !(await bestaetige(
        `Vorlage auf „${neu.name}" wechseln? Jeder Wert, den du nicht selbst gesetzt hast, folgt dann diesem Standard. Offene Monate rechnen danach neu; fertiggestellte bleiben, wie sie sind.`,
        { ok: 'Vorlage wechseln' },
      ))
    )
      return;
    setError(null);
    try {
      setVorlageStand(await setKvTemplate(schluessel));
    } catch {
      setError('Die Vorlage konnte nicht gewechselt werden.');
    }
  };

  // Stichtage, an denen es eigene Sätze gibt — zum schnellen Springen.
  const staende = useMemo(
    () =>
      Array.from(
        new Set(
          (saetze ?? [])
            .filter((s) => s.collectiveAgreementId === (kvId || null))
            .map((s) => s.validFrom),
        ),
      ).sort(),
    [saetze, kvId],
  );

  return (
    <div className={pageWide}>
      <div>
        <h1 className="text-3xl font-bold text-foreground">Einstellungen der Lohnverrechnung</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Jede Änderung wird gespeichert, sobald du das Feld verlässt. Ein leeres Feld stellt den vorigen Wert
          wieder her.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-border bg-muted/30 p-3">
        <div className="space-y-1.5">
          <label className={kopf} htmlFor="settings-gueltig-ab">
            Gültig ab
            <Hilfe>
              Änderungen gelten ab diesem Datum. Frühere Monate rechnen weiter mit den Werten, die damals galten.
              Die Maske zeigt, was an diesem Tag gilt. Willst du einen Wert nur ab einem späteren Monat ändern,
              wähle zuerst dieses Datum.
            </Hilfe>
          </label>
          <input
            id="settings-gueltig-ab"
            type="date"
            className={`${eingabe} w-auto`}
            value={datum}
            onChange={(e) => e.target.value && setDatum(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <label className={kopf} htmlFor="settings-vorlage">
            Kollektivvertrag
            <Hilfe>
              Die Vorlage bringt den Standard eines Kollektivvertrags mit: Sätze, Schwellen und Zulagen samt ihrer Fundstelle. Wo
              du nichts änderst, gilt dieser Standard — auch ein neuer Jahressatz kommt von selbst. Ein eigener Wert geht der
              Vorlage vor und bleibt, bis du das Feld wieder leerst.
              {artVorlage ? ' Die gewählte Entlohnungsart trägt ihre eigene Vorlage; sie wird bei den Entlohnungsarten eingestellt.' : ''}
            </Hilfe>
          </label>
          <select
            id="settings-vorlage"
            className={`${auswahl} w-auto max-w-[22rem]`}
            value={vorlage.schluessel}
            disabled={gesperrt || !!artVorlage}
            title={`${vorlage.name} — Fassung ${vorlage.fassung}`}
            onChange={(e) => void wechsleVorlage(e.target.value)}
          >
            {KV_VORLAGEN.map((v) => (
              <option key={v.schluessel} value={v.schluessel}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
        {agreements.length > 0 && (
          <div className="space-y-1.5">
            <label className={kopf} htmlFor="settings-kv">
              Entlohnungsart
              <Hilfe>
                „Alle" gilt für den ganzen Betrieb. Ein Wert für eine bestimmte Entlohnungsart (z. B. einen zweiten
                Kollektivvertrag) geht bei deren Mitarbeitern dem allgemeinen Wert vor.
              </Hilfe>
            </label>
            <select
              id="settings-kv"
              className={`${auswahl} w-auto`}
              value={kvId}
              onChange={(e) => setKvId(e.target.value)}
            >
              <option value="">alle</option>
              {agreements.map((kv) => (
                <option key={kv.id} value={kv.id}>
                  {kv.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {staende.length > 0 && (
          // Eigene Zeile: Neben Gültig-ab, Vorlage und Entlohnungsart bliebe den Marken nur eine schmale Spalte.
          <div className="w-full space-y-1.5">
            <span className={kopf}>Eigene Stände</span>
            <div className="flex flex-wrap gap-1.5">
              {staende.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDatum(d)}
                  className={`rounded-full border px-2.5 py-0.5 text-xs tabular-nums transition-colors ${
                    d === datum
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  ab {formatIsoDate(d)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-1 gap-y-2 border-b border-border">
        {REITER.map((r) => (
          <button
            key={r.id}
            type="button"
            role="tab"
            aria-selected={reiter === r.id}
            onClick={() => {
              const neu = new URLSearchParams(params);
              neu.set('tab', r.id);
              setParams(neu, { replace: true });
            }}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              reiter === r.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {r.label}
          </button>
        ))}
        <span className="ml-auto flex gap-3 pb-1 text-sm">
          <Link to="/payroll/absence-types" className="text-muted-foreground hover:text-foreground hover:underline">
            Fehlzeit-Arten
          </Link>
          <Link to="/payroll/holidays" className="text-muted-foreground hover:text-foreground hover:underline">
            Feiertage
          </Link>
        </span>
      </div>

      {error && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {!saetze && !error && <p className="text-sm text-muted-foreground">Laden…</p>}

      {saetze && (
        // Ein Stichtag, eine Stelle: Beim Wechsel beginnt die Maske neu, statt fremde Basiswerte mitzunehmen.
        <Maske
          key={`${datum}:${kvId}:${vorlage.schluessel}`}
          saetze={saetze}
          datum={datum}
          kvId={kvId || null}
          vorlage={vorlage}
          reiter={reiter}
          gesperrt={gesperrt}
          neuLaden={laden}
        />
      )}

      {/* G2: kein Rechenwert, deshalb außerhalb von Gültig-ab und Reitern. */}
      <AbschlussEinstellung />
      {/* I: ebenfalls kein Rechenwert — und eine Entscheidung der Firma, nicht ihres Lohnbüros. */}
      <ZettelLesenEinstellung />
    </div>
  );
}

// ── Die Maske zu einem Stichtag ─────────────────────────────────────────────

interface Umfeld {
  stelle: Stelle;
  gesperrt: boolean;
  /** Die KV-Vorlage der Maske — ihr Zulagenkatalog bestimmt die Karten im Reiter Zulagen. */
  vorlage: KvVorlage;
  /** Zeile unter dem Feld: Vorgabe, Herkunft, künftiger Wechsel. */
  unterzeile: (key: string, format: (text: string) => string) => ReactNode;
}

const UmfeldKontext = createContext<Umfeld | null>(null);

function useUmfeld(): Umfeld {
  const u = useContext(UmfeldKontext);
  if (!u) throw new Error('Einstellungsfeld außerhalb der Maske');
  return u;
}

function Maske({
  saetze,
  datum,
  kvId,
  vorlage,
  reiter,
  gesperrt,
  neuLaden,
}: {
  saetze: PayrollSetting[];
  datum: string;
  kvId: string | null;
  vorlage: KvVorlage;
  reiter: ReiterId;
  gesperrt: boolean;
  neuLaden: () => Promise<PayrollSetting[] | null>;
}) {
  // Die Warteschlange der Stelle arbeitet zwischen zwei Renderings weiter — sie
  // braucht den Stand nach dem letzten Schreiben, nicht den der letzten Anzeige.
  const aktuell = useRef(saetze);
  aktuell.current = saetze;

  const daten = useMemo(() => aufloesung(saetze, datum, kvId, vorlage), [saetze, datum, kvId, vorlage]);
  const standard = useMemo(() => vorlageWerte(vorlage, datum), [vorlage, datum]);

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `payrollSettings:${datum}:${kvId ?? 'alle'}:${vorlage.schluessel}`,
    bezeichnung: `Einstellungen der Lohnverrechnung ab ${formatIsoDate(datum)}`,
    daten,
    schreibe: async (patch) => {
      const vorher = aufloesung(aktuell.current, datum, kvId, vorlage);
      for (const key of schluesselFuer(vorlage)) {
        if (!(key in patch)) continue;
        const neu = alsText(key, patch[key]);
        if (neu !== null && neu === alsText(key, vorher[key])) continue;
        const hier = aktuell.current.find(
          (s) => s.key === key && s.validFrom === datum && s.collectiveAgreementId === kvId,
        );
        if (neu === null) {
          if (hier) await deleteSetting(hier.id);
        } else if (hier) {
          await api.patch(`/payroll/settings/${hier.id}`, { value: neu }, mitStand(hier.updatedAt));
        } else {
          await createSetting({ key, value: neu, validFrom: datum, collectiveAgreementId: kvId ?? undefined });
        }
      }
      const s = (await neuLaden()) ?? aktuell.current;
      aktuell.current = s;
      return aufloesung(s, datum, kvId, vorlage);
    },
  }) as Stelle;

  const unterzeile = useCallback(
    (key: string, format: (text: string) => string) => {
      const teile: string[] = [];
      const von = quelle(saetze, key, datum, kvId);
      if (von) teile.push(`eigener Wert seit ${formatIsoDate(von.validFrom)}`);
      // Der Standard der Vorlage mit seiner Fundstelle — und, wo vorgemerkt, sein nächster Jahressatz.
      // Die Fundstelle einer Zulage steht an ihrem Katalogeintrag, nicht an den einzelnen Schlüsseln.
      const stelleImVertrag =
        vorlage.fundstellen[key] ?? vorlage.zulagen.find((z) => Object.values(zulageSchluessel(z.schluessel)).includes(key))?.fundstelle;
      teile.push(`${vorlage.kurz}: ${format(standard[key])}${stelleImVertrag ? ` (${stelleImVertrag})` : ''}`);
      const jahreswechsel = vorlageNaechsterWert(vorlage, key, datum);
      if (jahreswechsel) teile.push(`${vorlage.kurz} ab ${formatIsoDate(jahreswechsel.gueltigAb)}: ${format(jahreswechsel.wert)}`);
      const spaeter = naechster(saetze, key, datum, kvId);
      if (spaeter) teile.push(`eigener Wert ab ${formatIsoDate(spaeter.validFrom)}: ${format(spaeter.value)}`);
      return teile.join(' · ');
    },
    [saetze, datum, kvId, vorlage, standard],
  );

  const umfeld = useMemo(() => ({ stelle, gesperrt, vorlage, unterzeile }), [stelle, gesperrt, vorlage, unterzeile]);

  return (
    <UmfeldKontext.Provider value={umfeld}>
      {reiter === 'arbeitszeit' && <ArbeitszeitReiter />}
      {reiter === 'ueberstunden' && <UeberstundenReiter />}
      {reiter === 'zulagen' && <ZulagenReiter />}
      {reiter === 'pausen' && <PausenReiter />}
    </UmfeldKontext.Provider>
  );
}

// ── Bausteine ───────────────────────────────────────────────────────────────

function Karte({ titel, hilfe, children }: { titel: string; hilfe?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <h2 className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
        {titel}
        {hilfe && <Hilfe>{hilfe}</Hilfe>}
      </h2>
      <div className="flex flex-wrap gap-x-6 gap-y-4">{children}</div>
    </section>
  );
}

function Beschriftung({ htmlFor, label, hilfe, feld }: { htmlFor: string; label: string; hilfe?: ReactNode; feld?: Feld<unknown> }) {
  return (
    <span className="flex items-center gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
        {label}
      </label>
      {hilfe && <Hilfe>{hilfe}</Hilfe>}
      {feld && <FeldStatusZeichen status={feld} />}
    </span>
  );
}

function ZahlFeld({
  k,
  label,
  einheit,
  hilfe,
  stellen = 2,
  min = 0,
  max,
  ganz,
  breite = 'w-28',
  deaktiviert,
}: {
  k: string;
  label: string;
  einheit?: string;
  hilfe?: ReactNode;
  stellen?: number;
  min?: number;
  max?: number;
  ganz?: boolean;
  breite?: string;
  deaktiviert?: boolean;
}) {
  const { stelle, gesperrt, unterzeile } = useUmfeld();
  const feld = useFeld<number | null>(stelle, k, {
    art: 'zahl',
    stellen,
    label,
    gesperrt: gesperrt || deaktiviert,
    pruefe: (w) => {
      if (w === null) return null;
      if (ganz && !Number.isInteger(w)) return 'Ganze Zahl';
      if (w < min) return `Mindestens ${zahlText(min)}`;
      if (max !== undefined && w > max) return `Höchstens ${zahlText(max)}`;
      return null;
    },
  });
  const id = `setting-${k}`;
  const format = (t: string) => `${zahlText(Number(t), stellen)}${einheit ? ` ${einheit}` : ''}`;
  return (
    <div className="space-y-1">
      <Beschriftung htmlFor={id} label={label} hilfe={hilfe} feld={feld as Feld<unknown>} />
      <div className="flex items-center gap-2">
        <input id={id} inputMode="decimal" className={`${eingabe} ${breite} text-right tabular-nums`} {...feld.props} />
        {einheit && <span className="text-sm text-muted-foreground">{einheit}</span>}
      </div>
      <p className="text-xs text-muted-foreground">{unterzeile(k, format)}</p>
      <FeldStatus status={feld} zeichen={false} />
    </div>
  );
}

function UhrzeitFeld({ k, label, hilfe }: { k: string; label: string; hilfe?: ReactNode }) {
  const { stelle, gesperrt, unterzeile } = useUmfeld();
  const feld = useFeld<string | null>(stelle, k, { art: 'text', lies: leseUhrzeit, pruefe: pruefeUhrzeit, label, gesperrt });
  const id = `setting-${k}`;
  return (
    <div className="space-y-1">
      <Beschriftung htmlFor={id} label={label} hilfe={hilfe} feld={feld as Feld<unknown>} />
      <input id={id} className={`${eingabe} w-28 text-center tabular-nums`} placeholder="HH:MM" {...feld.props} />
      <p className="text-xs text-muted-foreground">{unterzeile(k, (t) => `${t} Uhr`)}</p>
      <FeldStatus status={feld} zeichen={false} />
    </div>
  );
}

function Schalter({ k, label, hilfe }: { k: string; label: string; hilfe?: ReactNode }) {
  const { stelle, gesperrt, unterzeile } = useUmfeld();
  const feld = useFeld<boolean>(stelle, k, { art: 'schalter', label, gesperrt });
  const id = `setting-${k}`;
  return (
    <div className="w-full space-y-1">
      <span className="flex items-center gap-2">
        <input
          id={id}
          type="checkbox"
          className="h-4 w-4 rounded border-border accent-[hsl(var(--primary))]"
          {...feld.schalter}
        />
        <Beschriftung htmlFor={id} label={label} hilfe={hilfe} feld={feld as Feld<unknown>} />
      </span>
      <p className="text-xs text-muted-foreground">{unterzeile(k, (t) => (t === '1' ? 'ein' : 'aus'))}</p>
      <FeldStatus status={feld} zeichen={false} />
    </div>
  );
}

// ── Reiter: Arbeitszeit ─────────────────────────────────────────────────────

const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function ArbeitszeitReiter() {
  const { stelle } = useUmfeld();
  const daten = useStellenDaten(stelle);
  return (
    <div className="space-y-4">
      <p className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        Wochenstunden, Tages-Soll und lange/kurze Wochen stehen je Mitarbeiter im{' '}
        <Link to="/payroll/work-schedules" className="text-primary underline">
          Arbeitszeitmodell
        </Link>
        .
      </p>
      <Karte
        titel="Zeit-Rundung"
        hilfe="Kommt und Geht werden auf den Rundungsschritt gerundet, bevor gerechnet wird. Standard: aus."
      >
        <Schalter k="rundung_aktiv" label="Zeit-Rundung aktiv" />
        <ZahlFeld
          k="rundung_schritt"
          label="Rundungsschritt"
          einheit="min"
          stellen={0}
          min={1}
          max={60}
          ganz
          deaktiviert={daten.rundung_aktiv !== true}
        />
      </Karte>
      <BetriebszeitenKarte />
      <Karte
        titel="24. und 31. Dezember"
        hilfe="An beiden Tagen endet die Arbeitszeit vorgezogen, das Entgelt für die Normalarbeitszeit läuft fort. Fortgezahlt wird der Teil des Tages-Solls, der nach dem Arbeitsende läge — gerechnet ab Betriebsbeginn — und als Nichtleistungszeit ausgewiesen. Wer länger arbeitet, bekommt auf diese Stunden den Zuschlag statt der Gutschrift. Ein Tag mit Urlaub oder anderer Fehlzeit bleibt, wie er erfasst ist."
      >
        <Schalter k="halbtag_aktiv" label="Arbeitsende vorgezogen" />
        <UhrzeitFeld k="halbtag_ende" label="Arbeitsende" />
        <ZahlFeld
          k="halbtag_prozent"
          label="Zuschlag für Arbeit danach"
          einheit="%"
          stellen={0}
          deaktiviert={daten.halbtag_aktiv !== true}
        />
      </Karte>
    </div>
  );
}

function BetriebszeitenKarte() {
  const { stelle, gesperrt, unterzeile } = useUmfeld();
  const daten = useStellenDaten(stelle);
  const gespeichert = useMemo(() => parseBetriebszeiten(daten.betriebszeiten as string | undefined), [daten.betriebszeiten]);
  const eintrag = useEntwurf<(Betriebszeit | null)[]>(stelle, {
    kennung: 'betriebszeiten',
    gespeichert,
    felder: ['betriebszeiten'],
    label: 'Betriebszeiten',
    gesperrt,
    pruefe: (e) => {
      for (let i = 0; i < 7; i++) {
        const z = e[i];
        if (!z) continue;
        if (pruefeUhrzeit(z.von) || pruefeUhrzeit(z.bis)) return `${WOCHENTAGE[i]}: Uhrzeit als HH:MM`;
        if (z.bis <= z.von) return `${WOCHENTAGE[i]}: „bis" liegt nicht nach „von"`;
      }
      return null;
    },
    operation: (e) => (a) => ({ ...a, betriebszeiten: JSON.stringify(e) }),
  });
  const zeiten = eintrag.entwurf ?? gespeichert;
  const aendere = (i: number, z: Betriebszeit | null) =>
    eintrag.setze((alt) => (alt ?? gespeichert).map((x, j) => (j === i ? z : x)));

  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm" {...eintrag.huelle}>
      <h2 className="mb-1 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
        Betriebszeiten (Normalarbeitszeit des Betriebs)
        <Hilfe>
          Arbeitsstunden außerhalb dieser Fenster sind zuschlagspflichtig und werden mit dem KV-Teiler gerechnet.
          Wegzeit außerhalb zählt nur beim Lenker als Überstunde. Tage ohne Betrieb (z. B. Samstag) gelten komplett
          als außerhalb.
        </Hilfe>
        <FeldStatusZeichen status={eintrag} />
      </h2>
      <p className="mb-3 text-xs text-muted-foreground">{unterzeile('betriebszeiten', () => 'Mo–Fr 07:00–17:00')}</p>
      <table className="text-sm">
        <thead className="text-xs text-muted-foreground">
          <tr>
            <th className="pb-1 pr-4 text-left font-medium">Tag</th>
            <th className="pb-1 pr-4 text-center font-medium">Betrieb</th>
            <th className="pb-1 pr-2 text-left font-medium">von</th>
            <th className="pb-1 text-left font-medium">bis</th>
          </tr>
        </thead>
        <tbody>
          {WOCHENTAGE.map((tag, i) => {
            const z = zeiten[i] ?? null;
            return (
              <tr key={tag}>
                <td className="py-0.5 pr-4">{tag}</td>
                <td className="py-0.5 pr-4 text-center">
                  <input
                    type="checkbox"
                    aria-label={`${tag}: Betrieb`}
                    className="h-4 w-4 accent-[hsl(var(--primary))]"
                    checked={z !== null}
                    disabled={gesperrt}
                    onChange={(e) => aendere(i, e.target.checked ? { von: '07:00', bis: '17:00' } : null)}
                  />
                </td>
                <td className="py-0.5 pr-2">
                  <input
                    type="time"
                    aria-label={`${tag} von`}
                    className={`${eingabe} w-28 py-1 tabular-nums`}
                    value={z?.von ?? ''}
                    disabled={gesperrt || z === null}
                    onChange={(e) => z && aendere(i, { ...z, von: e.target.value })}
                  />
                </td>
                <td className="py-0.5">
                  <input
                    type="time"
                    aria-label={`${tag} bis`}
                    className={`${eingabe} w-28 py-1 tabular-nums`}
                    value={z?.bis ?? ''}
                    disabled={gesperrt || z === null}
                    onChange={(e) => z && aendere(i, { ...z, bis: e.target.value })}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <FeldStatus status={eintrag} zeichen={false} />
    </section>
  );
}

// ── Reiter: Überstunden & Zuschläge ─────────────────────────────────────────

const ZEITRAEUME = [
  { value: 'taeglich', label: 'Täglich' },
  { value: 'woechentlich', label: 'Wöchentlich' },
  { value: 'monatlich', label: 'Monatlich' },
  { value: 'monate', label: 'Alle x Monate' },
];

const ZEITRAUM_HINWEIS: Record<string, string> = {
  taeglich:
    'Jede Stunde über dem Tages-Soll wird sofort als Mehrzeit vergütet (9 statt 8 geplante Stunden ⇒ 1 h sofort). Der Satz richtet sich nach der Beschäftigungsart: Teilzeit der Teilzeit-Zuschlag, Vollzeit der Mehrarbeits-Zuschlag. Die Wochenschwellen gelten weiterhin — ab der Überstundenschwelle wird daraus eine Überstunde.',
  woechentlich:
    'Mehrzeit entsteht erst, wenn die Woche über den vereinbarten Wochenstunden liegt. Die Überstunden sind die zuletzt geleisteten Stunden der Woche.',
  monatlich:
    'Abgerechnet wird nur der Saldo am Monatsende; die Wochenschwellen werden dafür auf den Monat hochgerechnet.',
  monate:
    'Abgerechnet wird nur der Saldo am Ende der Periode; die Wochenschwellen werden auf die Periode hochgerechnet. Die Perioden beginnen im Jänner.',
};

function UeberstundenReiter() {
  const { stelle, gesperrt, unterzeile } = useUmfeld();
  const daten = useStellenDaten(stelle);
  const zeitraum = useFeld<string | null>(stelle, 'ueberstunden_zeitraum', { art: 'auswahl', label: 'Abrechnungszeitraum', gesperrt });
  const gewaehlt = (zeitraum.wert as string | null) ?? 'woechentlich';
  const zaehler = Number(daten.umrechnung_zaehler) || 0;
  const nenner = Number(daten.umrechnung_nenner) || 0;
  const faktor = nenner > 0 ? zaehler / nenner : 1;
  const zeitraumLabel = (t: string) => ZEITRAEUME.find((z) => z.value === t)?.label ?? t;

  return (
    <div className="space-y-4">
      <Karte titel="Abrechnungszeitraum">
        <div className="w-full space-y-1">
          <Beschriftung
            htmlFor="setting-ueberstunden_zeitraum"
            label="Mehrarbeit & Überstunden abrechnen"
            feld={zeitraum as Feld<unknown>}
            hilfe={ZEITRAUM_HINWEIS[gewaehlt] ?? ZEITRAUM_HINWEIS.woechentlich}
          />
          <div className="flex flex-wrap items-start gap-6">
            <select id="setting-ueberstunden_zeitraum" className={`${auswahl} w-56`} {...zeitraum.props}>
              {ZEITRAEUME.map((z) => (
                <option key={z.value} value={z.value}>
                  {z.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">{unterzeile('ueberstunden_zeitraum', zeitraumLabel)}</p>
          <p className="max-w-2xl text-xs text-muted-foreground">{ZEITRAUM_HINWEIS[gewaehlt]}</p>
          <FeldStatus status={zeitraum} zeichen={false} />
        </div>
        {gewaehlt === 'monate' && (
          <ZahlFeld k="ueberstunden_zeitraum_monate" label="Länge der Periode" einheit="Monate" stellen={0} min={2} max={12} ganz />
        )}
      </Karte>

      <Karte
        titel="Wochenschwellen"
        hilfe="Von den vereinbarten Wochenstunden bis zur Mehrarbeitsschwelle gilt der Teilzeit-Zuschlag, von dort bis zur Überstundenschwelle der Mehrarbeits-Zuschlag; darüber sind es Überstunden. Überstunden gibt es nur über der Überstundenschwelle — alles darunter ist Mehrarbeit."
      >
        <ZahlFeld k="mehrarbeit_schwelle" label="Mehrarbeitsschwelle" einheit="h/Woche" max={80} />
        <ZahlFeld k="ueberstunden_schwelle" label="Überstundenschwelle" einheit="h/Woche" max={80} />
        <ZahlFeld
          k="mehrzeit_zuschlag_teilzeit"
          label="Mehrzeitzuschlag Teilzeit"
          einheit="%"
          stellen={0}
          hilfe="Für Stunden über den vereinbarten Wochenstunden einer Teilzeitkraft, bis zur Mehrarbeitsschwelle."
        />
        <ZahlFeld
          k="mehrzeit_zuschlag_vollzeit"
          label="Mehrzeitzuschlag"
          einheit="%"
          stellen={0}
          hilfe="Für Stunden zwischen Mehrarbeits- und Überstundenschwelle (z. B. 38,5–40 h)."
        />
      </Karte>

      <Karte
        titel="Umrechnung (KV-Teiler)"
        hilfe={`Überstunden und zuschlagspflichtige Stunden werden als (Stunden × Zähler/Nenner) × (1 + Zuschlag) ausgewiesen. Aktuell ${zaehler}/${nenner} = Faktor ${faktor.toLocaleString('de-DE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}.`}
      >
        <ZahlFeld k="umrechnung_zaehler" label="Zähler" stellen={0} min={1} ganz breite="w-24" />
        <ZahlFeld k="umrechnung_nenner" label="Nenner" stellen={0} min={1} ganz breite="w-24" />
        <div className="space-y-1 self-center text-sm text-muted-foreground">
          Faktor{' '}
          <span className="font-medium tabular-nums text-foreground">
            {faktor.toLocaleString('de-DE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
          </span>
        </div>
      </Karte>

      <Karte titel="Zuschläge">
        <ZahlFeld
          k="zuschlag_ueberstunde"
          label="Basissatz je Überstunde"
          einheit="%"
          stellen={0}
          hilfe="Zuschlag, den jede Überstunde mindestens bekommt. Höhere Sätze aus den Zuschlagsregeln gehen vor."
        />
        <ZahlFeld
          k="zuschlag_ausserhalb_betriebszeit"
          label="Außerhalb der Betriebszeit"
          einheit="%"
          stellen={0}
          hilfe="Für Arbeitsstunden außerhalb der Betriebszeiten (Reiter Arbeitszeit). 0 % = aus."
        />
        <ZahlFeld k="sonntag_prozent" label="Sonntagszuschlag" einheit="%" stellen={0} hilfe="0 % = aus." />
        <ZahlFeld k="feiertag_prozent" label="Feiertagszuschlag" einheit="%" stellen={0} hilfe="0 % = aus." />
      </Karte>

      <ZuschlagRegelnKarte />

      <Karte
        titel="Sonderfälle der Überstunde"
        hilfe="Wann eine Überstunde abweichend von den Regeln oben bewertet wird. Jede Zeile ist ein eigener Schalter; je Stunde zählt weiter der höchste zutreffende Satz."
      >
        <Schalter
          k="ueberstunde_ab_zaehlt_mehrarbeit"
          label="Für „ab der x. Überstunde“ zählt die Mehrarbeit des Tages mit"
          hilfe="Hat ein Tag 1,5 h Mehrarbeit und danach Überstunden, beginnt die „dritte Überstunde“ schon nach einer halben Überstunde."
        />
        <Schalter
          k="mehrarbeit_ueber_50_ist_ueberstunde"
          label="Mehrarbeit mit mehr als 50 % Zuschlag gilt als Überstunde"
          hilfe="Fällt Mehrarbeit (38,5–40 h) in eine Zeit, für die ihr als Überstunde mehr als 50 % zustünden — nachts, am Sonntag —, wird sie als Überstunde ausgewiesen. Ausgeschaltet bleibt Mehrarbeit immer Mehrarbeit."
        />
        <ZahlFeld
          k="ueberstunde_100_arbeitsfrei_ab_stunde"
          label="Arbeitsfreier Tag: 100 % ab Arbeitsstunde"
          stellen={0}
          max={24}
          ganz
          hilfe="An einem Tag ohne Soll (z. B. Samstag) gilt „ab der x. Überstunde“ nicht; 100 % gibt es erst ab dieser Arbeitsstunde des Tages. 0 = wie an einem Arbeitstag."
        />
        <ZahlFeld
          k="ueberstunde_100_ab_wochenstunde"
          label="100 % ab Wochenstunde"
          stellen={0}
          max={100}
          ganz
          hilfe="Überstunden ab dieser Arbeitsstunde der Kalenderwoche tragen 100 % — auch über den Monatswechsel. 0 = aus."
        />
        <ZahlFeld
          k="rueckruf_prozent"
          label="Überstunden bei Rückruf"
          einheit="%"
          stellen={0}
          hilfe="Für die Überstunden eines Tages, an dem in der Monatserfassung „Rückruf“ eingetragen ist."
        />
      </Karte>
    </div>
  );
}

/** Eine Regelliste in Worten — für die Zeile mit dem Standard der Vorlage. */
function regelnText(json: string): string {
  let regeln: ZuschlagRegel[] = [];
  try {
    const a = JSON.parse(json);
    if (Array.isArray(a)) regeln = a;
  } catch {
    return json;
  }
  if (regeln.length === 0) return 'keine Regel';
  return regeln
    .map((r) =>
      r.art === 'zeit'
        ? `${r.von}–${r.bis} ${r.prozent} %${r.nur === 'ueberstunden' ? ' nur für Überstunden' : ''}`
        : `ab der ${r.abStunde}. Überstunde ${r.prozent} %`,
    )
    .join(', ');
}

function ZuschlagRegelnKarte() {
  const { stelle, gesperrt, unterzeile } = useUmfeld();
  const daten = useStellenDaten(stelle);
  const gespeichert = useMemo<ZuschlagRegel[]>(() => {
    try {
      const a = JSON.parse((daten.zuschlag_regeln as string | undefined) ?? '[]');
      return Array.isArray(a) ? a : [];
    } catch {
      return [];
    }
  }, [daten.zuschlag_regeln]);
  const eintrag = useEntwurf<ZuschlagRegel[]>(stelle, {
    kennung: 'zuschlag_regeln',
    gespeichert,
    felder: ['zuschlag_regeln'],
    label: 'Zuschlagsregeln',
    gesperrt,
    pruefe: (e) => {
      for (const [i, r] of e.entries()) {
        if (!Number.isFinite(r.prozent) || r.prozent < 0) return `Regel ${i + 1}: Zuschlag ab 0 %`;
        if (r.art === 'zeit' && (pruefeUhrzeit(r.von) || pruefeUhrzeit(r.bis))) return `Regel ${i + 1}: Uhrzeit als HH:MM`;
        if (r.art === 'ueberstunde_ab' && (!Number.isInteger(r.abStunde) || r.abStunde < 1))
          return `Regel ${i + 1}: ab der 1. Überstunde oder später`;
      }
      return null;
    },
    operation: (e) => (a) => ({ ...a, zuschlag_regeln: JSON.stringify(e) }),
  });
  const regeln = eintrag.entwurf ?? gespeichert;
  const aendere = (i: number, patch: Partial<{ von: string; bis: string; abStunde: number; prozent: number; nur: 'ueberstunden' | undefined }>) =>
    eintrag.setze((alt) => (alt ?? gespeichert).map((r, j) => (j === i ? ({ ...r, ...patch } as ZuschlagRegel) : r)));
  const zahl = (t: string) => {
    const n = Number(t.replace(',', '.'));
    return t.trim() === '' ? NaN : n;
  };

  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm" {...eintrag.huelle}>
      <h2 className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
        Zuschlagsregeln
        <Hilfe>
          Ein Zeitfenster gilt für alle darin gearbeiteten Stunden (z. B. Nacht 20:00–06:00) — oder, mit dem Haken
          „nur Überstunden", allein für Überstunden im Fenster. „Ab X. Überstunde" gilt nur für Überstunden. Je Stunde
          zählt der höchste zutreffende Satz — Zuschläge werden nicht addiert.
        </Hilfe>
        <FeldStatusZeichen status={eintrag} />
      </h2>
      <div className="space-y-2">
        {regeln.length === 0 && <p className="text-sm text-muted-foreground">Keine Regel — es gelten nur die Sätze oben.</p>}
        {regeln.map((r, i) => (
          <div key={i} className="flex flex-wrap items-end gap-3 rounded-md border border-border px-3 py-2">
            <span className="w-28 pb-2 text-sm text-muted-foreground">{r.art === 'zeit' ? 'Zeitfenster' : 'ab Überstunde'}</span>
            {r.art === 'zeit' ? (
              <>
                <label className="space-y-1 text-xs text-muted-foreground">
                  <span className="block">von</span>
                  <input type="time" className={`${eingabe} w-28 py-1`} value={r.von} disabled={gesperrt} onChange={(e) => aendere(i, { von: e.target.value })} />
                </label>
                <label className="space-y-1 text-xs text-muted-foreground">
                  <span className="block">bis</span>
                  <input type="time" className={`${eingabe} w-28 py-1`} value={r.bis} disabled={gesperrt} onChange={(e) => aendere(i, { bis: e.target.value })} />
                </label>
                <label className="flex items-center gap-1.5 pb-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border accent-[hsl(var(--primary))]"
                    checked={r.nur === 'ueberstunden'}
                    disabled={gesperrt}
                    onChange={(e) => aendere(i, { nur: e.target.checked ? 'ueberstunden' : undefined })}
                  />
                  nur Überstunden
                </label>
              </>
            ) : (
              <label className="space-y-1 text-xs text-muted-foreground">
                <span className="block">ab der … Überstunde</span>
                <input
                  inputMode="numeric"
                  className={`${eingabe} w-28 py-1 text-right tabular-nums`}
                  value={Number.isFinite(r.abStunde) ? String(r.abStunde) : ''}
                  disabled={gesperrt}
                  onChange={(e) => aendere(i, { abStunde: zahl(e.target.value) })}
                />
              </label>
            )}
            <label className="space-y-1 text-xs text-muted-foreground">
              <span className="block">Zuschlag in %</span>
              <input
                inputMode="decimal"
                className={`${eingabe} w-24 py-1 text-right tabular-nums`}
                value={Number.isFinite(r.prozent) ? String(r.prozent).replace('.', ',') : ''}
                disabled={gesperrt}
                onChange={(e) => aendere(i, { prozent: zahl(e.target.value) })}
              />
            </label>
            {!gesperrt && (
              <button
                type="button"
                onClick={() => eintrag.setze((alt) => (alt ?? gespeichert).filter((_, j) => j !== i))}
                className="mb-1 rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                aria-label={`Regel ${i + 1} entfernen`}
                title="Regel entfernen"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
        {!gesperrt && (
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={() => eintrag.setze((alt) => [...(alt ?? gespeichert), { art: 'zeit', von: '20:00', bis: '06:00', prozent: 100 }])}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted"
            >
              <Plus size={12} /> Zeitfenster
            </button>
            <button
              type="button"
              onClick={() => eintrag.setze((alt) => [...(alt ?? gespeichert), { art: 'ueberstunde_ab', abStunde: 3, prozent: 100 }])}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted"
            >
              <Plus size={12} /> Ab X. Überstunde
            </button>
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{unterzeile('zuschlag_regeln', regelnText)}</p>
      <FeldStatus status={eintrag} zeichen={false} />
    </section>
  );
}

// ── Reiter: Zulagen ─────────────────────────────────────────────────────────

const EINHEIT_TEXT: Record<ZulageDef['einheit'], string> = { h: 'Stunde', Nacht: 'Nächtigung', km: 'Kilometer' };

/** Eine Zulage des Katalogs: Schalter, Satz und — wo sie sich am Fenster bemisst — dessen Grenzen. */
function ZulageKarte({ def }: { def: ZulageDef }) {
  const { stelle } = useUmfeld();
  const daten = useStellenDaten(stelle);
  const k = zulageSchluessel(def.schluessel);
  const an = daten[k.aktiv] === true;
  const wie =
    def.bemessung === 'erfasste_stunden'
      ? 'Erfasst werden die Stunden je Tag in der Monatserfassung (Spalte einblenden).'
      : def.bemessung === 'markierte_tage_naz'
        ? 'Der Tag wird in der Monatserfassung angehakt (Spalte einblenden).'
        : def.bemessung === 'fensterstunden_ohne_ueberstunden'
          ? 'Wird aus Kommt und Geht gerechnet — keine eigene Eingabe.'
          : def.bemessung === 'naechtigung'
            ? 'Wird aus dem Kennzeichen „Nächtigung“ gerechnet.'
            : 'Wird aus den erfassten Kilometern gerechnet.';
  return (
    <div className="flex w-full flex-wrap items-start gap-x-6 gap-y-3 border-t border-border pt-3 first:border-t-0 first:pt-0">
      <div className="min-w-[18rem] flex-1">
        <Schalter k={k.aktiv} label={def.name} hilfe={`${def.hinweis ?? ''} ${wie}`.trim()} />
      </div>
      <ZahlFeld
        k={k.satz}
        label={def.prozent ? 'Satz vom Lohn' : `Satz je ${EINHEIT_TEXT[def.einheit]}`}
        einheit={def.prozent ? '%' : '€'}
        stellen={3}
        deaktiviert={!an}
      />
      {def.bemessung === 'fensterstunden_ohne_ueberstunden' && (
        <>
          <UhrzeitFeld k={k.von} label="von" />
          <UhrzeitFeld k={k.bis} label="bis" />
        </>
      )}
    </div>
  );
}

function ZulagenReiter() {
  const { vorlage } = useUmfeld();
  return (
    <div className="space-y-4">
      {vorlage.zulagen.length > 0 && (
        <Karte
          titel={`Zulagen laut ${vorlage.kurz}`}
          hilfe="Jede Zulage hat einen Schalter und einen Satz. Der Satz der Vorlage ist der Mindestsatz des Vertrags — zahlt der Betrieb mehr, trägst du seinen Satz ein. Eine eingeschaltete Zulage bietet in der Monatserfassung ihre Spalten an (Knopf „Spalten“): die Eingabe am Tag und das Ergebnis. Ausgewiesen werden Menge und Betrag — im Raster, in der Summenzeile und in der Aufschlüsselung darunter."
        >
          {vorlage.zulagen.map((z) => (
            <ZulageKarte key={z.schluessel} def={z} />
          ))}
        </Karte>
      )}
      <Karte
        titel="Montagezulage"
        hilfe="Die Menge wird berechnet: Montagezeit × Satz. Montagezeit = (Rückfahrt bzw. Geht) − (Abfahrt bzw. Kommt) − Reisezeit − Pause."
      >
        <ZahlFeld k="montagezulage_satz" label="Satz je Montagestunde" stellen={3} hilfe="Standard nach KV Metallgewerbe 2026, VIII/5." />
      </Karte>
      <Karte
        titel="Entfernungszulage"
        hilfe="Wird automatisch ermittelt: Montagezeit + Reisezeit über der kleinen Schwelle → kleine Zulage, über der mittleren Schwelle → mittlere Zulage. Reisen mit Nächtigung: Zwischentage große Zulage, Anreisetag je nach Abfahrtszeit, Rückreisetag je nach Ankunft im Betrieb (Rückfahrt + Fahrzeit)."
      >
        <ZahlFeld k="entfernungszulage_klein" label="Kleine Zulage" einheit="je Tag" />
        <ZahlFeld k="entfernungszulage_mittel" label="Mittlere Zulage" einheit="je Tag" />
        <ZahlFeld k="entfernungszulage_gross" label="Große Zulage" einheit="je Tag" />
        <div className="w-full" />
        <ZahlFeld k="entfernungszulage_schwelle_klein" label="Kleine Zulage ab Dauer über" einheit="h" max={24} />
        <ZahlFeld k="entfernungszulage_schwelle_mittel" label="Mittlere Zulage ab Dauer über" einheit="h" max={24} />
        <div className="w-full" />
        <UhrzeitFeld
          k="entfernungszulage_anreise_grenze"
          label="Anreisetag: Abfahrt vor"
          hilfe="Wer am Anreisetag vor dieser Uhrzeit abfährt, bekommt die große Zulage, sonst die mittlere."
        />
        <UhrzeitFeld
          k="entfernungszulage_rueckreise_grenze"
          label="Rückreisetag: Ankunft nach"
          hilfe="Wer am Rückreisetag nach dieser Uhrzeit im Betrieb ankommt, bekommt die große Zulage, sonst die mittlere."
        />
        <div className="w-full" />
        <Schalter
          k="entfernungszulage_klein_mit_pause"
          label="Kleine Zulage: Die Pause zählt zur Abwesenheit"
          hilfe="Eingeschaltet bemisst sich die kleine Zulage an der ununterbrochenen Abwesenheit von der Abfahrt bis zur Rückkehr — samt Pause. Ausgeschaltet zählt nur Montagezeit plus Reisezeit. Für die mittlere Zulage bleibt die Pause in beiden Fällen abgezogen."
        />
        <ZahlFeld
          k="entfernungszulage_verpflegung_prozent"
          label="Große Zulage bei beigestellter Verpflegung"
          einheit="%"
          stellen={0}
          max={100}
          hilfe="Stellt der Betrieb an einem Tag mit Nächtigung die Verpflegung, bleibt dieser Anteil der großen Zulage. Wirkt an Tagen, an denen in der Monatserfassung „Verpflegung gestellt“ eingetragen ist."
        />
      </Karte>
    </div>
  );
}

// ── Reiter: Pausen ──────────────────────────────────────────────────────────

function PausenReiter() {
  const { stelle } = useUmfeld();
  const daten = useStellenDaten(stelle);
  return (
    <Karte
      titel="Pausen"
      hilfe="Die Pausendauer kann je Tag in der Monatserfassung eingetragen werden. Bleibt das Feld dort leer, greift die folgende automatische Regel."
    >
      <Schalter k="pause_aktiv" label="Automatischer Pausenabzug" hilfe="Standard: ein — 30 min ab mehr als 6 h Arbeitszeit (§ 11 AZG)." />
      <ZahlFeld k="pause_dauer" label="Abzugsdauer" einheit="min" stellen={0} ganz max={240} hilfe="Wird auch als Vorschlag in der Erfassung verwendet." />
      <ZahlFeld k="pause_schwelle" label="ab Arbeitszeit über" einheit="h" max={24} deaktiviert={daten.pause_aktiv !== true} />
    </Karte>
  );
}

